/**
 * useUserProfile Hook
 * Manage user profile fetching and state
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import type { UserProfile, FeedbackPayload } from '../types';
import {
  fetchUserProfile as fetchUserProfileService,
  submitFeedback as submitFeedbackService,
  normalizeUserProfileError,
} from '../services/userProfileService';
import { useUserProfileState } from '../stores/useAppStore';
import { useMsalAuth } from '../auth/useMsalAuth';

const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface UseUserProfileOptions {
  enabled?: boolean;
}

export function useUserProfile({ enabled = true }: UseUserProfileOptions = {}) {
  const { account } = useMsalAuth();
  const { userProfile, setLoading, setSuccess, setError, clearError, reset } = useUserProfileState();

  const accountKey = useMemo(
    () => account?.homeAccountId ?? account?.username ?? null,
    [account?.homeAccountId, account?.username]
  );

  // Reset store when the active account changes
  const prevAccountKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevAccountKeyRef.current !== null && prevAccountKeyRef.current !== accountKey) {
      reset();
    }
    prevAccountKeyRef.current = accountKey;
  }, [accountKey, reset]);

  // Use a ref to guard against concurrent fetches without putting
  // userProfile.loading in the deps array (which would cancel the request
  // the moment setLoading(true) updates the store).
  const fetchInFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled || !accountKey) return;

    const hasValidCache =
      userProfile.data !== null &&
      userProfile.accountKey === accountKey &&
      userProfile.lastFetchedAt !== null &&
      Date.now() - userProfile.lastFetchedAt < PROFILE_CACHE_TTL_MS;

    if (hasValidCache || fetchInFlightRef.current) return;

    let cancelled = false;

    const load = async () => {
      fetchInFlightRef.current = true;
      setLoading(true, accountKey);
      clearError();
      try {
        const data = await fetchUserProfileService();
        if (!cancelled) {
          setSuccess(
            {
              displayName: data.displayName ?? '',
              email: data.email ?? '',
              avatarUrl: data.avatarUrl,
            },
            accountKey,
            Date.now()
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(normalizeUserProfileError(err), accountKey);
        }
      } finally {
        fetchInFlightRef.current = false;
        if (!cancelled) {
          setLoading(false, accountKey);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [
    accountKey,
    clearError,
    enabled,
    setError,
    setLoading,
    setSuccess,
    userProfile.accountKey,
    userProfile.data,
    userProfile.lastFetchedAt,
  ]);

  const fallbackName =
    account?.name?.trim() ||
    account?.username?.split('@')[0]?.replace(/[._-]+/g, ' ') ||
    'User';
  const fallbackEmail = account?.username ?? '';

  return {
    profile: userProfile.data
      ? ({
          id: accountKey ?? '',
          displayName: userProfile.data.displayName || fallbackName,
          email: userProfile.data.email || fallbackEmail,
          avatarUrl: userProfile.data.avatarUrl,
        } as UserProfile)
      : null,
    isLoading: userProfile.loading,
    error: userProfile.error?.message ?? null,
  };
}

export function useApplicationDisclaimer(userId?: string) {
  const [disclaimerContent, setDisclaimerContent] = useState<{ id: string; content: string; title: string; timeoutMs: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disclaimerTimeoutRef = useRef<number | undefined>(undefined);

  const startDisclaimerTimer = useCallback((timeoutMs: number) => {
    if (disclaimerTimeoutRef.current) {
      window.clearTimeout(disclaimerTimeoutRef.current);
    }

    if (timeoutMs > 0) {
      disclaimerTimeoutRef.current = window.setTimeout(() => {
        setHasAcknowledged(false);
      }, timeoutMs);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchContent = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { getApplicationDisclaimer, getDisclaimerExpiry } = await import('../services/userProfileService');
        const content = await getApplicationDisclaimer();
        if (cancelled) return;

        const expiryTime = getDisclaimerExpiry(userId);
        const expiresInMs = expiryTime - Date.now();

        setDisclaimerContent(content);
        setHasAcknowledged(expiresInMs > 0);

        if (expiresInMs > 0) {
          startDisclaimerTimer(expiresInMs);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to fetch disclaimer';
        setError(message);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchContent();
    return () => {
      cancelled = true;
      if (disclaimerTimeoutRef.current) window.clearTimeout(disclaimerTimeoutRef.current);
    };
  }, [startDisclaimerTimer, userId]);

  const acknowledge = useCallback(async () => {
    setIsAcknowledging(true);
    setError(null);
    try {
      const { acknowledgeApplicationDisclaimer } = await import('../services/userProfileService');
      await acknowledgeApplicationDisclaimer(userId, disclaimerContent?.timeoutMs ?? 0);
      setHasAcknowledged(true);
      startDisclaimerTimer(disclaimerContent?.timeoutMs ?? 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to acknowledge disclaimer';
      setError(message);
      throw err;
    } finally {
      setIsAcknowledging(false);
    }
  }, [disclaimerContent?.timeoutMs, startDisclaimerTimer, userId]);

  return { disclaimerContent, isLoading, isAcknowledging, hasAcknowledged, error, acknowledge };
}

export function useApplicationSettings() {
  const [applicationSettings, setApplicationSettings] = useState<{
    userGuideLinks: string[];
    systemCardLinks: string[];
    allowAgentSharing: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { getApplicationSettings } = await import('../services/userProfileService');
        const settings = await getApplicationSettings();
        setApplicationSettings({
          userGuideLinks: Array.isArray(settings?.userGuideLinks) ? settings.userGuideLinks : [],
          systemCardLinks: Array.isArray(settings?.systemCardLinks) ? settings.systemCardLinks : [],
          allowAgentSharing: Boolean(settings?.Agents?.allowAgentSharing),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch application settings';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { applicationSettings, isLoading, error };
}

export function useFeedback() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (payload: FeedbackPayload) => {
    setIsSubmitting(true);
    setError(null);
    try {
      return await submitFeedbackService(payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit feedback';
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { isSubmitting, error, submit };
}

// Fallback theme mapping in case API fails
const FALLBACK_THEME_MAP = new Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>([
  ['light', { id: '00000000-0000-0000-0000-000000000001', isDark: false, variables: {} }],
  ['dark', { id: '00000000-0000-0000-0000-000000000002', isDark: true, variables: {} }],
]);

export function usePreferences() {
  const [preferences, setPreferences] = useState<{
    theme: string;
    themeId: string; // Store the actual theme UUID
    language: string;
    memoryEnabled: boolean;
  }>({
    theme: 'light',
    themeId: '',
    language: 'en',
    memoryEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize with fallback so it's never empty
  const [themeMap, setThemeMap] = useState<Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>>(FALLBACK_THEME_MAP);
  const hasInitializedThemes = useRef(false);

  // Load initial preferences from cached profile or localStorage
  useEffect(() => {
    const cached = localStorage.getItem('kci.userPreferences.v1');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Ensure theme is either 'dark' or 'light', default to 'light'
        const theme = (parsed.theme === 'dark' || parsed.theme === 'light') ? parsed.theme : 'light';
        setPreferences({
          theme,
          themeId: parsed.themeId || '',
          language: parsed.language || 'en',
          memoryEnabled: parsed.memoryEnabled ?? false,
        });
      } catch (e) {
        console.warn('Failed to parse cached preferences', e);
      }
    }
    setIsLoading(false);
  }, []);

  // Fetch available themes on mount to get ID mapping
  useEffect(() => {
    if (hasInitializedThemes.current) return;
    hasInitializedThemes.current = true;

    const fetchThemes = async () => {
      type AvailableTheme = {
        Id: string;
        Name: string;
        IsDefault?: boolean;
        Properties?: { Variables?: Record<string, string> };
      };

      try {
        const { getAvailableThemes: getAvailableThemesService } = await import('../services/userProfileService');
        const themes = (await getAvailableThemesService()) as AvailableTheme[];
        const mapping = new Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>();
        let defaultTheme: AvailableTheme | null = null;
        
        themes.forEach((theme) => {
          const lowerName = theme.Name.toLowerCase();
          const isDark = lowerName.includes('dark') || lowerName.includes('midnight');
          
          const themeInfo = {
            id: theme.Id,
            isDark,
            variables: theme.Properties?.Variables || {},
          };
          
          console.log(`[Theme] Loaded theme "${theme.Name}" (${theme.Id}) with ${Object.keys(themeInfo.variables).length} CSS variables`);
          
          // Map both full name and simplified variant
          mapping.set(lowerName, themeInfo);
          
          // Map by variant: for Light use 'light', for Dark/Midnight use 'dark'
          if (lowerName === 'light') {
            mapping.set('light', themeInfo);
            if (theme.IsDefault || !defaultTheme) {
              defaultTheme = theme;
            }
          } else if (isDark) {
            // Store the first dark-like theme as 'dark', but keep originals too
            if (!mapping.has('dark')) {
              mapping.set('dark', themeInfo);
            }
            if (theme.IsDefault && !lowerName.includes('light')) {
              defaultTheme = theme;
            }
          }
          
          // Cache the theme variables in sessionStorage for quick access
          const varsToCache = theme.Properties?.Variables || {};
          console.log(`[usePreferences] Caching ${Object.keys(varsToCache).length} CSS variables for theme ${theme.Id}`);
          sessionStorage.setItem(`theme-vars-${theme.Id}`, JSON.stringify(varsToCache));
        });
        
        setThemeMap(mapping);
        
        // If current user preferences don't have a themeId, set one from default theme
        setPreferences((currentPrefs) => {
          if (defaultTheme && !currentPrefs.themeId) {
            const lowerDefault = defaultTheme.Name.toLowerCase();
            const themeVariant = (lowerDefault.includes('dark') || lowerDefault.includes('midnight')) ? 'dark' : 'light';
            const updated = { ...currentPrefs, themeId: defaultTheme.Id, theme: themeVariant };
            localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
            console.log(`[Theme] Set default theme: ${themeVariant} (${defaultTheme.Id})`);
            return updated;
          }
          return currentPrefs;
        });
      } catch (err) {
        console.warn('Failed to fetch theme mapping, using fallback:', err);
        // Use fallback theme map on error
        setThemeMap(FALLBACK_THEME_MAP);
      }
    };
    fetchThemes();
  }, []);

  const updateTheme = useCallback(async (themeVariant: 'dark' | 'light' | string) => {
    console.log(`[updateTheme] Called with variant: "${themeVariant}"`);
    setIsLoading(true);
    setError(null);
    try {
      const { updateTheme: updateThemeService } = await import('../services/userProfileService');
      
      // Look up the theme info for this variant
      // Try themeMap first, fall back to FALLBACK_THEME_MAP
      let themeInfo = themeMap.get(themeVariant.toLowerCase());
      if (!themeInfo && themeMap.size === 0) {
        themeInfo = FALLBACK_THEME_MAP.get(themeVariant.toLowerCase());
      }
      
      if (!themeInfo) {
        throw new Error(`Theme variant "${themeVariant}" not found in available themes`);
      }
      
      console.log(`[updateTheme] Found theme info:`, themeInfo);
      
      // POST the UUID to backend
      await updateThemeService(themeInfo.id);
      
      // Cache theme variables if available
      if (themeInfo.variables) {
        sessionStorage.setItem(`theme-vars-${themeInfo.id}`, JSON.stringify(themeInfo.variables));
      }
      
      // Store both the variant (dark/light) and the UUID
      const updated = { ...preferences, theme: themeVariant, themeId: themeInfo.id };
      console.log(`[updateTheme] Setting preferences to:`, updated);
      setPreferences(updated);
      localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
      console.log(`[updateTheme] ✓ Theme updated successfully`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update theme';
      setError(message);
      console.error('Theme update error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [preferences, themeMap]);

  const updateLanguage = useCallback(async (language: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { updateLanguage: updateLanguageService } = await import('../services/userProfileService');
      await updateLanguageService(language);
      
      const updated = { ...preferences, language };
      setPreferences(updated);
      localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update language';
      setError(message);
      console.error('Language update error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [preferences]);

  // Local memory preference (no backend endpoint)
  const updateMemory = useCallback(async (memoryEnabled: boolean) => {
    const updated = { ...preferences, memoryEnabled };
    setPreferences(updated);
    localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
  }, [preferences]);

  return { 
    preferences, 
    isLoading, 
    error, 
    updateTheme, 
    updateLanguage, 
    updateMemory 
  };
}
